import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, Phone, MapPin, Users as UsersIcon, Crown, Repeat, Eye, Settings, ArrowUpRight, ArrowDownLeft, Wallet, Shield, Check, AlertTriangle, RotateCcw, Pencil, Filter, X, ChevronDown } from "lucide-react";
import { formatDateTime } from "@/lib/format";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import PlaceManagementDialog from "@/components/PlaceManagementDialog";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BLANK_FORM = {
  name: "",
  place: "",
  phone1: "",
  phone2: "",
};

export default function Customers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMaster = user?.role === "master";
  
  // Tab state: "overview" or "manage"
  const [activeTab, setActiveTab] = useState("overview");
  
  const [items, setItems] = useState([]);
  const [todayCustomers, setTodayCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stats, setStats] = useState({});
  const [topValue, setTopValue] = useState([]);
  const [topFreq, setTopFreq] = useState([]);
  const [placeDialogOpen, setPlaceDialogOpen] = useState(false);
  const [loadingToday, setLoadingToday] = useState(false);
  
  // Credit balance lists
  const [customersOweUs, setCustomersOweUs] = useState([]);
  const [customersWeOwe, setCustomersWeOwe] = useState([]);
  const [creditSummary, setCreditSummary] = useState({ totalReceivable: 0, totalPayable: 0 });
  const [loadingCredit, setLoadingCredit] = useState(true); // Start true - data loads on mount
  
  // Manage tab state
  const [allCustomers, setAllCustomers] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [manageFilter, setManageFilter] = useState("");
  
  // Manage tab filters
  const [placeFilter, setPlaceFilter] = useState("");
  const [balanceFilter, setBalanceFilter] = useState("all"); // "all", "owes", "credit"
  const [allPlaces, setAllPlaces] = useState([]);
  
  // Bulk action dialogs
  const [bulkResetOpen, setBulkResetOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // Refs for Enter key navigation
  const nameInputRef = useRef(null);
  const phone1InputRef = useRef(null);
  const phone2InputRef = useRef(null);
  const submitBtnRef = useRef(null);

  // Determine which list to display
  const isSearching = q.trim().length > 0;
  const displayItems = isSearching ? items : todayCustomers;

  const loadSearch = async () => {
    if (!q.trim()) {
      setItems([]);
      return;
    }
    try {
      const { data } = await api.get("/customers", { params: { q: q.trim() } });
      const arr = data.items || [];
      setItems(arr);
      // Load stats for search results
      const ids = arr.slice(0, 50).map((c) => c.id).join(",");
      if (ids) {
        try {
          const { data: bs } = await api.get("/customers/stats", { params: { ids } });
          setStats((prev) => ({ ...prev, ...bs.stats }));
        } catch (_e) { /* ignore */ }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadTodayCustomers = async () => {
    setLoadingToday(true);
    try {
      const { data } = await api.get("/customers/today");
      const arr = data.items || [];
      setTodayCustomers(arr);
      // Load stats for today's customers
      const ids = arr.slice(0, 50).map((c) => c.id).join(",");
      if (ids) {
        try {
          const { data: bs } = await api.get("/customers/stats", { params: { ids } });
          setStats((prev) => ({ ...prev, ...bs.stats }));
        } catch (_e) { /* ignore */ }
      }
    } catch (e) {
      console.error("Failed to load today's customers:", e);
    } finally {
      setLoadingToday(false);
    }
  };

  const loadTop = async () => {
    try {
      const [v, f] = await Promise.all([
        api.get("/customers/top", { params: { by: "value", limit: 20 } }),
        api.get("/customers/top", { params: { by: "frequency", limit: 20 } }),
      ]);
      setTopValue(v.data.items || []);
      setTopFreq(f.data.items || []);
    } catch (_e) {
      /* silent */
    }
  };

  const loadCreditBalances = async () => {
    setLoadingCredit(true);
    try {
      const { data } = await api.get("/credit/pending", { params: { limit: 100 } });
      const items = data.items || [];
      
      // Check for warning or error from backend
      if (data.warning) {
        console.warn("[Credit] API warning:", data.warning);
        toast.warning(data.warning);
      }
      if (data.error) {
        console.error("[Credit] API error:", data.error);
        toast.error(`Failed to load credit data: ${data.error}`);
      }
      
      // Customers who owe us (receivable > 0)
      const oweUs = items
        .filter((c) => (c.receivable || 0) > 0.01)
        .sort((a, b) => (b.receivable || 0) - (a.receivable || 0));
      
      // Customers we owe (payable > 0)
      const weOwe = items
        .filter((c) => (c.payable || 0) > 0.01)
        .sort((a, b) => (b.payable || 0) - (a.payable || 0));
      
      setCustomersOweUs(oweUs);
      setCustomersWeOwe(weOwe);
      setCreditSummary({
        totalReceivable: data.total_receivable || 0,
        totalPayable: data.total_payable || 0,
      });
    } catch (e) {
      console.error("[Credit] Failed to load credit balances:", e);
      toast.error("Failed to load credit balances. Please refresh.");
    } finally {
      setLoadingCredit(false);
    }
  };

  // Load all customers for Manage tab (MASTER only)
  const loadAllCustomers = async () => {
    if (!isMaster) return;
    setLoadingAll(true);
    try {
      const { data } = await api.get("/customers/all");
      setAllCustomers(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingAll(false);
    }
  };

  // Load all places for filter dropdown
  const loadAllPlaces = async () => {
    try {
      const { data } = await api.get("/places/all");
      setAllPlaces(data.items || []);
    } catch (_e) {
      /* silent */
    }
  };

  // Bulk reset ledger
  const handleBulkReset = async () => {
    if (confirmText !== "RESET_LEDGER") {
      toast.error("Type RESET_LEDGER to confirm");
      return;
    }
    setBulkProcessing(true);
    try {
      const { data } = await api.post("/customers/bulk/reset-ledger", {
        customer_ids: Array.from(selectedIds),
        confirmation: "RESET_LEDGER",
      });
      toast.success(data.message);
      setBulkResetOpen(false);
      setConfirmText("");
      setSelectedIds(new Set());
      loadAllCustomers();
      loadCreditBalances();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkProcessing(false);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (confirmText !== "DELETE_CUSTOMERS") {
      toast.error("Type DELETE_CUSTOMERS to confirm");
      return;
    }
    setBulkProcessing(true);
    try {
      const { data } = await api.post("/customers/bulk/delete", {
        customer_ids: Array.from(selectedIds),
        confirmation: "DELETE_CUSTOMERS",
      });
      toast.success(data.message);
      setBulkDeleteOpen(false);
      setConfirmText("");
      setSelectedIds(new Set());
      loadAllCustomers();
      loadTodayCustomers();
      loadTop();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkProcessing(false);
    }
  };

  // Toggle select all
  const toggleSelectAll = () => {
    const filtered = filteredAllCustomers;
    if (selectedIds.size === filtered.length && filtered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((c) => c.id)));
    }
  };

  // Toggle single selection
  const toggleSelect = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  // Filter for manage tab - includes text search, place filter, and balance filter
  const filteredAllCustomers = allCustomers.filter((c) => {
    // Text search filter
    if (manageFilter.trim()) {
      const needle = manageFilter.toLowerCase();
      const matchesText = (
        (c.name || "").toLowerCase().includes(needle) ||
        (c.place || "").toLowerCase().includes(needle) ||
        (c.phone1 || "").includes(needle) ||
        (c.phone2 || "").includes(needle)
      );
      if (!matchesText) return false;
    }
    
    // Place filter
    if (placeFilter) {
      if ((c.place || "").toLowerCase() !== placeFilter.toLowerCase()) {
        return false;
      }
    }
    
    // Balance filter
    if (balanceFilter === "owes") {
      if (!((c.receivable || 0) > 0.01)) return false;
    } else if (balanceFilter === "credit") {
      if (!((c.payable || 0) > 0.01)) return false;
    }
    
    return true;
  });
  
  // Get unique places from customers for the dropdown (sorted)
  const uniquePlacesFromCustomers = [...new Set(
    allCustomers
      .map(c => c.place)
      .filter(p => p && p.trim())
  )].sort((a, b) => a.localeCompare(b));
  
  // Clear all filters
  const clearAllFilters = () => {
    setManageFilter("");
    setPlaceFilter("");
    setBalanceFilter("all");
  };
  
  const hasActiveFilters = manageFilter.trim() || placeFilter || balanceFilter !== "all";

  useEffect(() => {
    const t = setTimeout(loadSearch, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => {
    loadTodayCustomers();
    loadTop();
    loadCreditBalances();
  }, []);

  // Load all customers when switching to manage tab
  useEffect(() => {
    if (activeTab === "manage" && isMaster && allCustomers.length === 0) {
      loadAllCustomers();
      loadAllPlaces();
    }
  }, [activeTab, isMaster]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        place: form.place.trim(),
        phone1: form.phone1.trim(),
        phone2: form.phone2.trim(),
      };
      if (editing) {
        await api.patch(`/customers/${editing}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer added");
      }
      setForm(BLANK_FORM);
      setEditing(null);
      // Reload today's customers (new customer will appear there)
      loadTodayCustomers();
      loadTop();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name || "",
      place: c.place || "",
      phone1: c.phone1 || "",
      phone2: c.phone2 || "",
    });
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer removed");
      loadTodayCustomers();
      if (isSearching) loadSearch();
      loadTop();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="fade-up" data-testid="customers-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">CRM</p>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl tracking-tight">Customers</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Retail and wholesale buyers — tracked across all shops.
            </p>
          </div>
          {isMaster && (
            <div className="flex gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={activeTab === "overview" ? "default" : "ghost"}
                size="sm"
                onClick={() => setActiveTab("overview")}
                className="h-8 px-3 text-xs"
                data-testid="tab-overview"
              >
                Overview
              </Button>
              <Button
                variant={activeTab === "manage" ? "default" : "ghost"}
                size="sm"
                onClick={() => setActiveTab("manage")}
                className="h-8 px-3 text-xs"
                data-testid="tab-manage"
              >
                <Shield className="w-3 h-3 mr-1.5" />
                Manage All
              </Button>
            </div>
          )}
        </div>
      </div>

      {activeTab === "overview" ? (
        <>
          {/* OVERVIEW TAB - Original content */}

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5 md:col-span-1" data-testid="customer-form-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            {editing ? "Edit customer" : "Add customer"}
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</Label>
              <Input
                ref={nameInputRef}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    // Focus Place autocomplete (it has its own internal input)
                    const placeInput = document.querySelector('[data-testid="place-autocomplete-input"]');
                    placeInput?.focus();
                  }
                }}
                className="mt-1"
                required
                data-testid="customer-name-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Place</Label>
                <a
                  href="/places"
                  className="inline-flex items-center h-5 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                  title="Manage places"
                  data-testid="manage-places-btn"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Manage
                </a>
              </div>
              <div className="mt-1">
                <PlaceAutocomplete
                  value={form.place}
                  onChange={(val) => setForm({ ...form, place: val })}
                  placeholder="Type 2+ chars to search places..."
                  onEnterToNext={() => phone1InputRef.current?.focus()}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone 1</Label>
                <Input
                  ref={phone1InputRef}
                  value={form.phone1}
                  onChange={(e) => setForm({ ...form, phone1: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      phone2InputRef.current?.focus();
                    }
                  }}
                  className="mt-1 font-mono-data"
                  data-testid="customer-phone1-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone 2</Label>
                <Input
                  ref={phone2InputRef}
                  value={form.phone2}
                  onChange={(e) => setForm({ ...form, phone2: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitBtnRef.current?.click();
                    }
                  }}
                  className="mt-1 font-mono-data"
                  data-testid="customer-phone2-input"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                ref={submitBtnRef}
                type="submit"
                disabled={saving}
                style={{ backgroundColor: "#9E6D5B" }}
                className="flex-1"
                data-testid="customer-save-btn"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Add customer"}
              </Button>
              {editing && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditing(null);
                    setForm(BLANK_FORM);
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </Card>

        <Card className="p-0 md:col-span-2 overflow-hidden" data-testid="customers-list-card">
          <div className="p-3 border-b border-[hsl(var(--border))]">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search any combination: name, phone, place... (e.g., 'amit luck' or '987 bang')"
              className="h-10"
              data-testid="customers-search-input"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-muted-foreground">
                {isSearching ? (
                  <>
                    <span className="font-medium text-foreground">{displayItems.length}</span> result{displayItems.length !== 1 ? "s" : ""} for &ldquo;<span className="font-medium">{q}</span>&rdquo;
                  </>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1">
                      <UsersIcon className="w-3 h-3" />
                      <span className="font-medium text-emerald-600">{displayItems.length}</span> new customer{displayItems.length !== 1 ? "s" : ""} added today
                    </span>
                  </>
                )}
              </span>
              {isSearching && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setQ("")}
                  className="h-6 px-2 text-xs"
                >
                  Clear search
                </Button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]">
                <tr className="text-left">
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Name</th>
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Contact</th>
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Lifetime</th>
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Last purchase</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {displayItems.map((c) => {
                  const s = stats[c.id];
                  return (
                    <tr key={c.id} className="border-t border-[hsl(var(--border))]" data-testid={`customer-row-${c.id}`}>
                      <td className="p-3">
                        <div className="font-medium">{c.name}</div>
                        {c.place && (
                          <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {c.place}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-xs font-mono-data text-muted-foreground">
                        {c.phone1 && (
                          <div className="inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {c.phone1}
                          </div>
                        )}
                        {c.phone2 && <div className="mt-0.5">{c.phone2}</div>}
                        {!c.phone1 && !c.phone2 && "—"}
                      </td>
                      <td className="p-3 text-right font-mono-data">
                        {s ? (
                          <>
                            <div className="font-semibold">₹{Number(s.total_spent || 0).toFixed(2)}</div>
                            <div className="text-[10px] text-muted-foreground">{s.count} sales</div>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {s?.last_purchase_at ? (
                          <>
                            <div>{formatDateTime(s.last_purchase_at)}</div>
                            <div className="font-mono-data">
                              ₹{Number(s.last_purchase_amount || 0).toFixed(2)} · {s.last_type}
                            </div>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/customers/${c.id}`)}
                          data-testid={`customer-view-${c.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEdit(c)}
                          data-testid={`customer-edit-${c.id}`}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => remove(c)}
                          data-testid={`customer-delete-${c.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!displayItems.length && (
                  <tr>
                    <td colSpan={5} className="p-10 text-center text-sm text-muted-foreground">
                      <UsersIcon className="w-6 h-6 mx-auto mb-2 opacity-40" />
                      {isSearching ? (
                        <>No customers found matching &ldquo;{q}&rdquo;</>
                      ) : loadingToday ? (
                        <>Loading today&apos;s customers...</>
                      ) : (
                        <>No new customers added today</>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Credit Balance Tables */}
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {/* Customers Who Owe Us (Receivable) */}
        <Card className="overflow-hidden" data-testid="customers-owe-us-table">
          <div className="p-3 border-b bg-red-50">
            <div className="flex items-center gap-2">
              <ArrowDownLeft className="w-4 h-4 text-red-600" />
              <h3 className="font-semibold text-sm text-red-800">Customers Who Owe Us</h3>
            </div>
            <p className="text-xs text-red-600 mt-0.5">
              Total Receivable: <span className="font-bold">₹{creditSummary.totalReceivable.toFixed(2)}</span>
            </p>
          </div>
          <div className="">
            <table className="w-full text-sm">
              <thead className="bg-red-50/50">
                <tr>
                  <th className="p-2 text-left text-xs font-medium text-red-700">Customer</th>
                  <th className="p-2 text-right text-xs font-medium text-red-700">Amount Owed</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {loadingCredit ? (
                  <tr>
                    <td colSpan={3} className="p-6 text-center text-xs text-muted-foreground">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin"></div>
                        Loading receivables...
                      </div>
                    </td>
                  </tr>
                ) : customersOweUs.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="p-6 text-center text-xs text-muted-foreground">
                      No outstanding receivables
                    </td>
                  </tr>
                ) : (
                  customersOweUs.map((c) => (
                    <tr key={c.customer_id} className="border-t hover:bg-red-50/30">
                      <td className="p-2">
                        <div className="font-medium text-xs">{c.name}</div>
                        {c.phone1 && <div className="text-[10px] text-muted-foreground">{c.phone1}</div>}
                      </td>
                      <td className="p-2 text-right font-mono text-xs font-semibold text-red-600">
                        ₹{(c.receivable || 0).toFixed(2)}
                      </td>
                      <td className="p-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => navigate(`/customers/${c.customer_id}`)}
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="p-2 border-t bg-red-50/30 text-[10px] text-red-600 text-right">
            {customersOweUs.length} customer{customersOweUs.length !== 1 ? "s" : ""}
          </div>
        </Card>

        {/* Customers We Owe (Store Credit / Payable) */}
        <Card className="overflow-hidden" data-testid="customers-we-owe-table">
          <div className="p-3 border-b bg-emerald-50">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-emerald-600" />
              <h3 className="font-semibold text-sm text-emerald-800">Customers With Store Credit</h3>
            </div>
            <p className="text-xs text-emerald-600 mt-0.5">
              Total Payable: <span className="font-bold">₹{creditSummary.totalPayable.toFixed(2)}</span>
            </p>
          </div>
          <div className="">
            <table className="w-full text-sm">
              <thead className="bg-emerald-50/50">
                <tr>
                  <th className="p-2 text-left text-xs font-medium text-emerald-700">Customer</th>
                  <th className="p-2 text-right text-xs font-medium text-emerald-700">Credit Balance</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {loadingCredit ? (
                  <tr>
                    <td colSpan={3} className="p-6 text-center text-xs text-muted-foreground">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin"></div>
                        Loading store credits...
                      </div>
                    </td>
                  </tr>
                ) : customersWeOwe.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="p-6 text-center text-xs text-muted-foreground">
                      No outstanding store credits
                    </td>
                  </tr>
                ) : (
                  customersWeOwe.map((c) => (
                    <tr key={c.customer_id} className="border-t hover:bg-emerald-50/30">
                      <td className="p-2">
                        <div className="font-medium text-xs">{c.name}</div>
                        {c.phone1 && <div className="text-[10px] text-muted-foreground">{c.phone1}</div>}
                      </td>
                      <td className="p-2 text-right font-mono text-xs font-semibold text-emerald-600">
                        ₹{(c.payable || 0).toFixed(2)}
                      </td>
                      <td className="p-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => navigate(`/customers/${c.customer_id}`)}
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="p-2 border-t bg-emerald-50/30 text-[10px] text-emerald-600 text-right">
            {customersWeOwe.length} customer{customersWeOwe.length !== 1 ? "s" : ""}
          </div>
        </Card>
      </div>

      {/* Top 20 tables */}
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <TopTable
          title="Top 20 · Lifetime value"
          subtitle="Customers ranked by total spend across all shops."
          icon={<Crown className="w-4 h-4 text-[#9E6D5B]" />}
          rows={topValue}
          valueKey="total_spent"
          valueLabel="Spend"
          testid="top-customers-value"
        />
        <TopTable
          title="Top 20 · Frequency"
          subtitle="Customers ranked by number of purchases."
          icon={<Repeat className="w-4 h-4 text-[#9E6D5B]" />}
          rows={topFreq}
          valueKey="visits"
          valueLabel="Visits"
          testid="top-customers-frequency"
        />
      </div>

      {/* Place Management Dialog */}
      <PlaceManagementDialog
        open={placeDialogOpen}
        onOpenChange={setPlaceDialogOpen}
      />
        </>
      ) : (
        /* MANAGE ALL TAB - Master only */
        <Card className="p-0 overflow-hidden" data-testid="manage-all-card">
          {/* Toolbar */}
          <div className="p-4 border-b bg-amber-50/50">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-600" />
                <div>
                  <h2 className="font-semibold text-amber-900">Bulk Customer Management</h2>
                  <p className="text-xs text-amber-700">
                    {allCustomers.length} total customers · {selectedIds.size} selected
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={manageFilter}
                  onChange={(e) => setManageFilter(e.target.value)}
                  placeholder="Filter by name, place, phone..."
                  className="h-8 w-48 text-sm"
                  data-testid="manage-filter-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadAllCustomers}
                  disabled={loadingAll}
                  className="h-8"
                  data-testid="manage-refresh-btn"
                >
                  <RotateCcw className={`w-3.5 h-3.5 ${loadingAll ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </div>
            
            {/* Filter row */}
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-amber-200 flex-wrap">
              <div className="flex items-center gap-1.5 text-xs text-amber-800">
                <Filter className="w-3.5 h-3.5" />
                <span className="font-medium">Filter by:</span>
              </div>
              
              {/* Place Filter */}
              <Select value={placeFilter || "all"} onValueChange={(v) => setPlaceFilter(v === "all" ? "" : v)}>
                <SelectTrigger 
                  className="h-8 w-44 text-xs bg-white"
                  data-testid="place-filter-select"
                >
                  <MapPin className="w-3 h-3 mr-1 text-muted-foreground" />
                  <SelectValue placeholder="All Places" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="all">All Places</SelectItem>
                  {uniquePlacesFromCustomers.map((place) => (
                    <SelectItem key={place} value={place}>
                      {place}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Balance Filter */}
              <Select value={balanceFilter} onValueChange={setBalanceFilter}>
                <SelectTrigger 
                  className="h-8 w-36 text-xs bg-white"
                  data-testid="balance-filter-select"
                >
                  <Wallet className="w-3 h-3 mr-1 text-muted-foreground" />
                  <SelectValue placeholder="All Balances" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Balances</SelectItem>
                  <SelectItem value="owes">
                    <span className="text-red-600 font-medium">Owes Us</span>
                  </SelectItem>
                  <SelectItem value="credit">
                    <span className="text-emerald-600 font-medium">Has Credit</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              {/* Clear filters button */}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="h-8 text-xs text-amber-700 hover:text-amber-900"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear Filters
                </Button>
              )}
              
              {/* Filter summary */}
              {hasActiveFilters && (
                <span className="text-xs text-amber-600 ml-auto">
                  Showing {filteredAllCustomers.length} of {allCustomers.length}
                </span>
              )}
            </div>
            
            {/* Bulk action buttons */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-amber-200">
                <span className="text-xs text-amber-800 font-medium">
                  With {selectedIds.size} selected:
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkResetOpen(true)}
                  className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50"
                  data-testid="bulk-reset-btn"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Reset Ledger to Zero
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkDeleteOpen(true)}
                  className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Delete Customers
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIds(new Set())}
                  className="h-7 text-xs"
                >
                  Clear Selection
                </Button>
              </div>
            )}
          </div>

          {/* Scrollable customer list */}
          <div className="h-[calc(100vh-320px)] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted sticky top-0 z-10">
                <tr className="text-left">
                  <th className="p-3 w-10">
                    <Checkbox
                      checked={filteredAllCustomers.length > 0 && selectedIds.size === filteredAllCustomers.length}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Name</th>
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Contact</th>
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Balance</th>
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingAll ? (
                  <tr>
                    <td colSpan={5} className="p-10 text-center text-sm text-muted-foreground">
                      Loading all customers...
                    </td>
                  </tr>
                ) : filteredAllCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-10 text-center text-sm text-muted-foreground">
                      <UsersIcon className="w-6 h-6 mx-auto mb-2 opacity-40" />
                      {manageFilter ? "No customers match your filter" : "No customers found"}
                    </td>
                  </tr>
                ) : (
                  filteredAllCustomers.map((c) => {
                    const hasBalance = Math.abs(c.balance || 0) > 0.01;
                    const isOwing = (c.receivable || 0) > 0.01;
                    const hasCredit = (c.payable || 0) > 0.01;
                    
                    return (
                      <tr
                        key={c.id}
                        className={`border-t hover:bg-muted/30 ${selectedIds.has(c.id) ? "bg-amber-50" : ""}`}
                        data-testid={`manage-row-${c.id}`}
                      >
                        <td className="p-3">
                          <Checkbox
                            checked={selectedIds.has(c.id)}
                            onCheckedChange={() => toggleSelect(c.id)}
                            data-testid={`select-${c.id}`}
                          />
                        </td>
                        <td className="p-3">
                          <div className="font-medium">{c.name}</div>
                          {c.place && (
                            <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {c.place}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-xs font-mono-data text-muted-foreground">
                          {c.phone1 && (
                            <div className="inline-flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {c.phone1}
                            </div>
                          )}
                          {c.phone2 && <div className="mt-0.5">{c.phone2}</div>}
                          {!c.phone1 && !c.phone2 && "—"}
                        </td>
                        <td className="p-3 text-right">
                          {hasBalance ? (
                            <div>
                              {isOwing && (
                                <div className="text-xs font-semibold text-red-600">
                                  Owes ₹{(c.receivable || 0).toFixed(2)}
                                </div>
                              )}
                              {hasCredit && (
                                <div className="text-xs font-semibold text-emerald-600">
                                  Credit ₹{(c.payable || 0).toFixed(2)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => navigate(`/customers/${c.id}`)}
                              title="View details"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                setActiveTab("overview");
                                startEdit(c);
                              }}
                              title="Edit"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="p-3 border-t bg-muted/50 text-xs text-muted-foreground flex items-center justify-between">
            <span>
              Showing {filteredAllCustomers.length} of {allCustomers.length} customers
            </span>
            {selectedIds.size > 0 && (
              <span className="font-medium text-amber-700">
                {selectedIds.size} selected
              </span>
            )}
          </div>
        </Card>
      )}

      {/* Bulk Reset Dialog */}
      <Dialog open={bulkResetOpen} onOpenChange={setBulkResetOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Reset Ledger for {selectedIds.size} Customer{selectedIds.size !== 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              This will <strong>permanently wipe all ledger history</strong> for the selected customers.
              All receivables and payables will be set to zero with no audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="bg-red-50 p-3 rounded border border-red-200">
              <p className="text-sm text-red-800 font-medium">This action cannot be undone.</p>
              <p className="text-xs text-red-600 mt-1">
                Type <code className="bg-red-100 px-1 rounded font-mono">RESET_LEDGER</code> to confirm
              </p>
            </div>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type RESET_LEDGER to confirm"
              className="font-mono"
              data-testid="confirm-reset-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkResetOpen(false); setConfirmText(""); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkReset}
              disabled={bulkProcessing || confirmText !== "RESET_LEDGER"}
              data-testid="confirm-reset-btn"
            >
              {bulkProcessing ? "Processing..." : "Reset Ledgers"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Delete {selectedIds.size} Customer{selectedIds.size !== 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              This will <strong>soft delete</strong> the selected customers.
              They will no longer appear in searches or reports.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="bg-red-50 p-3 rounded border border-red-200">
              <p className="text-sm text-red-800 font-medium">This action cannot be easily undone.</p>
              <p className="text-xs text-red-600 mt-1">
                Type <code className="bg-red-100 px-1 rounded font-mono">DELETE_CUSTOMERS</code> to confirm
              </p>
            </div>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE_CUSTOMERS to confirm"
              className="font-mono"
              data-testid="confirm-delete-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkDeleteOpen(false); setConfirmText(""); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkProcessing || confirmText !== "DELETE_CUSTOMERS"}
              data-testid="confirm-delete-btn"
            >
              {bulkProcessing ? "Processing..." : "Delete Customers"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TopTable({ title, subtitle, icon, rows, valueKey, valueLabel, testid }) {
  return (
    <Card className="p-0 overflow-hidden" data-testid={testid}>
      <div className="p-4 border-b border-[hsl(var(--border))]">
        <div className="flex items-center gap-2 font-heading tracking-tight">{icon} {title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))]">
            <tr className="text-left">
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium w-10">#</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Customer</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.customer_id} className="border-t border-[hsl(var(--border))]" data-testid={`${testid}-row-${i}`}>
                <td className="p-3 font-mono-data text-xs text-muted-foreground">{i + 1}</td>
                <td className="p-3">
                  <div className="font-medium">{r.name || "—"}</div>
                  {r.place && <div className="text-[11px] text-muted-foreground">{r.place}</div>}
                </td>
                <td className="p-3 text-right font-mono-data">
                  {valueKey === "total_spent"
                    ? `₹${Number(r.total_spent || 0).toFixed(2)}`
                    : Number(r.visits || 0)}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={3} className="p-8 text-center text-sm text-muted-foreground">
                  No sales yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
